import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { IconAddressBook } from '@tabler/icons-react';
import { BetaSchemaForm, ProFormColumnsType } from '@ant-design/pro-components';
import { CrudMode, renderMultiListToTags } from '@ohpen/enso-ui';
import { Button, FormInstance, Tag, Form } from 'antd';
import { EditOutlined, CloseOutlined } from '@ant-design/icons';

import { TabProps } from '../interfaces';
import { FirmGetById } from '@pmp/api';
import { CrudFormCard } from './CrudFormCard';
import { formatAddressAsLines, getHighlightProps } from '../utils';
import { SchemaFormCheckbox } from '@pmp/utils';

const QUALIFIED_DOMAINS_STORAGE_KEY = 'firms:qualified-domains';

export const ContactDetails: React.FC<TabProps> = ({ form, mode, selectsOptions, fcaModifiedFields, removeFcaModifiedField }) => {
  const { t } = useTranslation();
  const readonly = mode === CrudMode.VIEW;
  const [isEditing, setIsEditing] = useState(false);
  const type = Form.useWatch(['registrationDetails', 'type'])

  const [knownDomains, setKnownDomains] = useState<string[]>(() => {
    try {
      const stored = localStorage.getItem(QUALIFIED_DOMAINS_STORAGE_KEY);
      return stored ? (JSON.parse(stored) as string[]) : [];
    } catch {
      return [];
    }
  });

  const onQualifiedDomainsChange = useCallback((values: string[]) => {
    setKnownDomains(prev => {
      const merged = Array.from(new Set([...prev, ...values]));
      localStorage.setItem(QUALIFIED_DOMAINS_STORAGE_KEY, JSON.stringify(merged));
      return merged;
    });
  }, []);

  const isFcaRegistered = useMemo(() => type !== 'NOT_FCA_REGISTERED', [type]);

  const addressTitle = useMemo(
    () => (
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <span>
          {t('lender.firms.labels.registeredOfficeAddress')}
          {isFcaRegistered && (
            <Tag color="blue" style={{ marginLeft: 8 }}>
              {t('lender.firms.labels.fca')}
            </Tag>
          )}
        </span>
        <div>
          {!readonly && (
            <Button type="link" icon={isEditing ? <CloseOutlined /> : <EditOutlined />} onClick={() => setIsEditing(!isEditing)} />
          )}
        </div>
      </div>
    ),
    [readonly, t, isEditing, isFcaRegistered],
  );

  const addressDetailColumns: ProFormColumnsType<FirmGetById, 'text'>[] = useMemo(
    () => [
      {
        valueType: 'group',
        title: addressTitle,
        columns: [],
        colProps: { span: 24 },
      },
      {
        valueType: 'group',
        columns: [
          {
            title: t('lender.firms.labels.addressLine1'),
            dataIndex: ['contactDetails', 'firmAddress', 'lineItems', 0],
            valueType: 'text',
            readonly,
            fieldProps: {
              placeholder: t('lender.firms.labels.addressLine1Placeholder'),
              className: getHighlightProps(['contactDetails', 'firmAddress'], fcaModifiedFields),
              onChange: () => {
                if (getHighlightProps(['contactDetails', 'firmAddress'], fcaModifiedFields)) {
                  removeFcaModifiedField?.('contactDetails', 'firmAddress');
                }
              },
            },
            formItemProps: {
              rules: [{ required: true }],
            },
            colProps: { span: 12 },
          },
          {
            title: t('lender.firms.labels.addressLine2'),
            dataIndex: ['contactDetails', 'firmAddress', 'lineItems', 1],
            valueType: 'text',
            readonly,
            fieldProps: {
              placeholder: t('lender.firms.labels.addressLine2Placeholder'),
              className: getHighlightProps(['contactDetails', 'firmAddress'], fcaModifiedFields),
              onBlur: () => removeFcaModifiedField?.('contactDetails', 'firmAddress'),
            },
            formItemProps: {
              rules: [{ required: true }],
            },
            colProps: { span: 12 },
          },
        ],
      },
      {
        valueType: 'group',
        columns: [
          {
            title: t('lender.firms.labels.addressLine3'),
            dataIndex: ['contactDetails', 'firmAddress', 'lineItems', 2],
            valueType: 'text',
            fieldProps: {
              className: getHighlightProps(['contactDetails', 'firmAddress'], fcaModifiedFields),
              onBlur: () => removeFcaModifiedField?.('contactDetails', 'firmAddress'),
            },
            readonly,
            colProps: { span: 12 },
          },
          {
            title: t('lender.firms.labels.addressLine4'),
            dataIndex: ['contactDetails', 'firmAddress', 'lineItems', 3],
            fieldProps: {
              className: getHighlightProps(['contactDetails', 'firmAddress'], fcaModifiedFields),
              onBlur: () => removeFcaModifiedField?.('contactDetails', 'firmAddress'),
            },
            valueType: 'text',
            readonly,
            colProps: { span: 12 },
          },
        ],
      },
      {
        valueType: 'group',
        columns: [
          {
            title: t('lender.firms.labels.town'),
            dataIndex: ['contactDetails', 'firmAddress', 'city'],
            valueType: 'text',
            fieldProps: {
              className: getHighlightProps(['contactDetails', 'firmAddress'], fcaModifiedFields),
              onBlur: () => removeFcaModifiedField?.('contactDetails', 'firmAddress'),
            },
            readonly,
            colProps: { span: 12 },
          },
          {
            title: t('lender.firms.labels.county'),
            dataIndex: ['contactDetails', 'firmAddress', 'lineItems', 4],
            fieldProps: {
              className: getHighlightProps(['contactDetails', 'firmAddress'], fcaModifiedFields),
              onBlur: () => removeFcaModifiedField?.('contactDetails', 'firmAddress'),
            },
            valueType: 'text',
            readonly,
            colProps: { span: 12 },
          },
        ],
      },
      {
        valueType: 'group',
        columns: [
          {
            title: t('lender.firms.labels.postCode'),
            dataIndex: ['contactDetails', 'firmAddress', 'postCode'],
            valueType: 'text',
            fieldProps: {
              className: getHighlightProps(['contactDetails', 'firmAddress'], fcaModifiedFields),
              onBlur: () => removeFcaModifiedField?.('contactDetails', 'firmAddress'),
            },
            readonly,
            colProps: { span: 12 },
          },
          {
            title: t('lender.firms.labels.country'),
            dataIndex: ['contactDetails', 'firmAddress', 'country'],
            valueType: 'text',
            fieldProps: { className: getHighlightProps(['contactDetails', 'firmAddress'], fcaModifiedFields) },
            onBlur: () => removeFcaModifiedField?.('contactDetails', 'firmAddress'),
            readonly,
            colProps: { span: 12 },
          },
        ],
      },
    ],
    [t, readonly, addressTitle, fcaModifiedFields, removeFcaModifiedField],
  );

  const columns: ProFormColumnsType<FirmGetById, 'text'>[] = useMemo(
    () => [
      {
        valueType: 'group',
        columns: [
          {
            title: t('lender.firms.labels.mobile'),
            dataIndex: ['contactDetails', 'mobilePhone'],
            valueType: 'text',
            fieldProps: {
              className: getHighlightProps(['contactDetails', 'mobilePhone'], fcaModifiedFields),
              onBlur: () => removeFcaModifiedField?.('contactDetails', 'mobilePhone'),
            },
            formItemProps: {
              label: (
                <span>
                  {t('lender.firms.labels.mobile')}
                  {isFcaRegistered && (
                    <Tag color="blue" style={{ marginLeft: 8 }}>
                      {t('lender.firms.labels.fca')}
                    </Tag>
                  )}
                </span>
              ),
            },
            readonly,
            colProps: { span: 12 },
          },
          {
            title: t('lender.firms.labels.telephone'),
            dataIndex: ['contactDetails', 'workPhone'],
            valueType: 'text',
            fieldProps: {
              className: getHighlightProps(['contactDetails', 'workPhone'], fcaModifiedFields),
              onBlur: () => removeFcaModifiedField?.('contactDetails', 'workPhone'),
            },
            formItemProps: {
              label: (
                <span>
                  {t('lender.firms.labels.telephone')}
                  {isFcaRegistered && (
                    <Tag color="blue" style={{ marginLeft: 8 }}>
                      {t('lender.firms.labels.fca')}
                    </Tag>
                  )}
                </span>
              ),
            },
            readonly,
            colProps: { span: 12 },
          },
        ],
      },
      {
        valueType: 'group',
        columns: [
          {
            title: t('lender.firms.labels.website'),
            dataIndex: ['contactDetails', 'website'],
            valueType: 'text',
            fieldProps: {
              className: getHighlightProps(['contactDetails', 'website'], fcaModifiedFields),
              onBlur: () => {
                removeFcaModifiedField?.('contactDetails', 'website');
              },
            },
            formItemProps: {
              label: (
                <span>
                  {t('lender.firms.labels.website')}
                  {isFcaRegistered && (
                    <Tag color="blue" style={{ marginLeft: 8 }}>
                      {t('lender.firms.labels.fca')}
                    </Tag>
                  )}
                </span>
              ),
            },
            readonly,
            colProps: { span: 12 },
          },
          {
            title: t('lender.firms.labels.complaintsWebPage'),
            dataIndex: ['contactDetails', 'complaintsWebsite'],
            valueType: 'text',
            readonly,
            colProps: { span: 12 },
          },
        ],
      },
      {
        title: t('lender.firms.labels.email'),
        dataIndex: ['contactDetails', 'email'],
        valueType: 'text',
        readonly,
        formItemProps: {
          rules: [{ required: true }],
        },
        colProps: { span: 12 },
      },
      {
        title: t('lender.firms.labels.qualifiedDomains'),
        dataIndex: ['contactDetails', 'qualifiedDomains'],
        valueType: 'select',
        readonly,
        render: renderMultiListToTags,
        fieldProps: {
          mode: 'tags',
          tokenSeparators: [','],
          notFoundContent: null,
          options: knownDomains.map(d => ({ label: d, value: d })),
          onChange: onQualifiedDomainsChange,
        },
        colProps: { span: 12 },
      },

      ...(!isEditing
        ? [
          {
            title: addressTitle,
            valueType: 'group' as const,
            columns: [],
            colProps: { span: 12 },
          },
          {
            dataIndex: ['contactDetails', 'firmAddress'],
            valueType: 'textarea' as const,
            fieldProps: (form: FormInstance) => {
              const addr = form?.getFieldValue(['contactDetails', 'firmAddress']);
              return {
                value: formatAddressAsLines(addr),
                autoSize: { minRows: 2 },
                disabled: true,
                className: getHighlightProps(['contactDetails', 'firmAddress'], fcaModifiedFields),
                onBlur: () => removeFcaModifiedField?.('contactDetails', 'firmAddress'),
              };
            },
            colProps: { span: 12 },
          },
        ]
        : addressDetailColumns),
    ],
    [readonly, t, addressDetailColumns, isEditing, addressTitle, fcaModifiedFields, removeFcaModifiedField, isFcaRegistered, knownDomains, onQualifiedDomainsChange],
  );

  return (
    <CrudFormCard icon={<IconAddressBook />} title={t('lender.firms.labels.contactDetails')}>
      <BetaSchemaForm<FirmGetById> form={form} layoutType="Embed" columns={columns} submitter={false} />
    </CrudFormCard>
  );
};
